import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Radio, DatePicker, NumberInputDropdown, WarningText, Checkbox } from 'components/UIElements'
import { fromUTCMidnightSeconds, toUTCMidnightSeconds } from 'utils/time'
import STRINGS from 'utils/strings'
import { pluralize } from 'utils/misc'
import { DEPLOYMENT_KEYS, DEPLOYMENT_VALUES } from 'utils/constants'
import { getChainDeploymentStudyScheduleIds } from 'utils/chainDeploymentUtils'
import { buildChainExpiryInfo } from 'utils/chainExpiryUtils'
import VisitExpireContent from './VisitExpireContent'
import dayOptions, { RECURRING_AFTER_TIME_OPTION_DEFAULT } from '../../constants/schedulerConstants'
import contentConnect from '../../containers/ContentContainer'
import InstrumentExpireVisitSelection from './InstrumentExpireVisitSelection'

const CHAIN_EXPIRE_OFFSET = DEPLOYMENT_KEYS.chainExpireOffset
const NEVER = DEPLOYMENT_KEYS.never
const NOW = DEPLOYMENT_KEYS.now
const RELATIVE_TO_FIRST_LOGIN = DEPLOYMENT_KEYS.firstLogin
const RELATIVE_TO_SEND_IDEAL = DEPLOYMENT_KEYS.relativeToSendIdeal
const RECEIPT_OF_ARTICLE = DEPLOYMENT_VALUES.receiptOfArticle
const COMPLETION_OF_VISIT = DEPLOYMENT_VALUES.completionOfVisit
const EXPIRE_BY_VISIT = DEPLOYMENT_VALUES.expireByVisit
const EXPIRE_TYPE = DEPLOYMENT_KEYS.expireType
const RECURRENCE_DEFAULT_VALUE = 2

export const _getDayString = num => `day${num !== 1 ? 's' : ''}`
const _getOccurenceString = num => (num !== 1 ? 'occurrences' : 'occurrence')



const _getExpireOnContent = ({
  expire,
  deploy,
  isInstrumentToInstrument,
  isChainExpire,
  isRelative,
  isRelativeToSendIdeal,
  updateExpireField,
  updateExpire,
  selected,
  updateRecurring,
  errors,
}) => {
  const isRelativeExpiration = isRelative || isChainExpire || isRelativeToSendIdeal
  const selectedDate = moment(expire.absolute ? fromUTCMidnightSeconds(expire.absolute) : moment().startOf('day'))
  const startDate = moment(deploy.absolute ? fromUTCMidnightSeconds(deploy.absolute) : undefined)

  const getRelativeExpirationVal = () => {
    if (isRelativeExpiration) {
      if (expire.first_login) {
        return expire.first_login.interval - deploy.first_login.interval
      }
      if (isChainExpire) {
        return expire.chain_expire_offset - deploy.chain_deploy_offset
      }
      if (isRelativeToSendIdeal) {
        return expire.relative_to_send_ideal?.interval - 1
      }
    }
    return null
  }

  const relExpVal = getRelativeExpirationVal()

  const input = isRelativeExpiration ? (
    <NumberInputDropdown
      value={relExpVal}
      options={dayOptions}
      onChange={num => {
        updateRecurring(null)
        if (isRelative) {
          updateExpireField(RELATIVE_TO_FIRST_LOGIN, {
            interval: num + deploy.first_login.interval,
            interval_type: 'days',
          })
        } else if (isInstrumentToInstrument) {
          updateExpireField(CHAIN_EXPIRE_OFFSET, num + deploy.chain_deploy_offset)
        } else if (isRelativeToSendIdeal) {
          updateExpireField(RELATIVE_TO_SEND_IDEAL, { interval: num + 1, interval_type: 'days' })
        }
      }}
      onBlur={e => {
        if (e.target.value === '') {
          if (isRelative) {
            updateExpireField(RELATIVE_TO_FIRST_LOGIN, {
              interval: 1 + deploy.first_login.interval,
              interval_type: 'days',
            })
          } else if (isInstrumentToInstrument) {
            updateExpireField(CHAIN_EXPIRE_OFFSET, 1 + deploy.chain_deploy_offset)
          } else if (isRelativeToSendIdeal) {
            updateExpireField(RELATIVE_TO_SEND_IDEAL, { interval: 1, interval_type: 'days' })
          }
        }
      }}
    />
  ) : (
    <DatePicker
      disabledDays={{ before: startDate.toDate() }}
      initialDate={selectedDate}
      onDayChange={date => {
        if (!date) return
        updateRecurring(null)
        updateExpire({ absolute: toUTCMidnightSeconds(date) })
      }}
    />
  )
  return (
    <div>
      <span>{isRelativeExpiration ? 'Expire' : 'Expire on Date'}</span>
      {(isRelativeExpiration || selected.expire_on) && input}
      {isRelativeExpiration ? `${pluralize(relExpVal, 'day', 'days', false)} after Instrument starts` : ''}
      <WarningText text={errors['expire-2']} />
    </div>
  )
}

const ExpireContent = props => {
  const {
    deploy,
    disabled,
    expire,
    isVisitSummary,
    isDeployedByVisit,
    isDiary,
    isInstrumentToInstrument,
    isRelative,
    isRelativeToInstrumentSentDate,
    recurring,
    updateExpire,
    updateExpireField,
    updateRecurring,
    updateRecurringField,
    updateScheduleKey,
    hasInstrumentUnification,
    chain_deployment_info,
    chain_expiry_info,
  } = props

  const expires =
    'absolute' in expire ||
    RELATIVE_TO_FIRST_LOGIN in expire ||
    RELATIVE_TO_SEND_IDEAL in expire ||
    EXPIRE_TYPE in expire
  const chainExpires = CHAIN_EXPIRE_OFFSET in expire
  const isImmediateStart = NOW in deploy
  const isSameRelativeDay = isRelative && expires && deploy.first_login.interval === expire.first_login.interval
  const isSameRelativeDayToSendIdeal =
    isRelativeToInstrumentSentDate && expires && expire.relative_to_send_ideal.interval === 1
  const isAfterRelativeDayToSendIdeal =
    isRelativeToInstrumentSentDate && expires && expire.relative_to_send_ideal.interval !== 1
  const isAfterVisit = isDeployedByVisit && expires && expire[EXPIRE_TYPE] === COMPLETION_OF_VISIT
  const isAfterReceiptOfArticle = isDeployedByVisit && expires && expire[EXPIRE_TYPE] === RECEIPT_OF_ARTICLE
  const isExpireByVisit = expires && expire[EXPIRE_TYPE] === EXPIRE_BY_VISIT

  const selected = {
    all_completed: NEVER in expire && (!recurring || !recurring.recurrences) && !expire[NEVER],
    expire_after_send_ideal: expires && isAfterRelativeDayToSendIdeal,
    expire_after: chainExpires,
    expire_on: expires && !(isSameRelativeDay || isSameRelativeDayToSendIdeal || isAfterRelativeDayToSendIdeal || isExpireByVisit),
    repeat_after: NEVER in expire && recurring && !!recurring.recurrences,
    same_day_send_ideal: expires && isSameRelativeDayToSendIdeal,
    same_day: expires && isSameRelativeDay,
    receipt_of_article: isAfterReceiptOfArticle,
    completion_of_visit: isAfterVisit,
    expire_by_visit: isExpireByVisit,
    never: NEVER in expire && expire[NEVER],
  }

  // For visit-triggered instruments that are already configured with "Never expire"
  // (i.e. the "When all complete / Never expire" option is selected),
  // ensure "Unless a visit confirmation applies" is selected by default the first time,
  // unless an explicit value has already been set.
  useEffect(() => {
    if (isDeployedByVisit && selected.all_completed && expire.expire_on_visit_confirmation === undefined) {
      updateExpireField('expire_on_visit_confirmation', true)
      const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
      if (!chain_expiry_info?.expiry_condition && !chain_expiry_info?.visit_id && !chain_expiry_info?.visits && deployArmIds.length > 0) {
        updateScheduleKey('chain_expiry_info', buildChainExpiryInfo({
          existing: chain_expiry_info,
          armIds: deployArmIds,
          visits: [],
        }))
      }
    }
  }, [
    isDeployedByVisit,
    selected.all_completed,
    expire.expire_on_visit_confirmation,
    chain_expiry_info?.study_schedule_id,
    chain_expiry_info?.study_schedule_ids,
    chain_deployment_info?.study_schedule_id,
    chain_deployment_info?.study_schedule_ids,
    updateExpireField,
    updateScheduleKey,
  ])

  const _updateRecurring = _recurring => {
    // if recurring is already selected just add recurrences default value
    let newRecurring = {}
    Object.assign(newRecurring, _recurring?.scheme ? _recurring : RECURRING_AFTER_TIME_OPTION_DEFAULT['n-days'])

    newRecurring.recurrences = RECURRENCE_DEFAULT_VALUE

    if (newRecurring.scheme === 'persistent') {
      delete newRecurring.scheme

      newRecurring = {
        ...newRecurring,
        interval: 1,
        interval_type: 'days',
        scheme: 'n-days',
        visible_days: 1,
      }
    }

    updateRecurring(newRecurring)
    updateExpire({ never: null })
  }

  return (
    <div className={`expire-menu${disabled ? ' disabled' : ''}`}>
      <p>
        <span>When should this instrument </span>
        <strong>expire</strong>
        <span>?</span>
      </p>
      <div>
        {!hasInstrumentUnification ? (
          <Radio
            id='when-all-complete'
            selected={selected.all_completed}
            onClick={() => {
              // For non-visit instruments, clear recurring when "Never expire" is chosen.
              // For visit-triggered instruments, we want a default 1-day recurring config
              // so that the repeat menu has a concrete interval instead of a null state.
              if (isDeployedByVisit) {
                const { recurrences, ...restRecurring } = recurring || {}
                const defaultRecurring =
                  Object.keys(restRecurring).length > 0 ? restRecurring : RECURRING_AFTER_TIME_OPTION_DEFAULT['n-days']
                updateRecurring(defaultRecurring)
              } else {
                updateRecurring(null)
              }

              // For "Never expire", clear any explicit expire configuration.
              updateExpire({ never: null })

              // Enable visit confirmation by default for visit-triggered instruments
              // when "Never expire" is first selected; user can later uncheck it.
              if (isDeployedByVisit && expire.expire_on_visit_confirmation === undefined) {
                updateExpireField('expire_on_visit_confirmation', true)
                // Initialize chain_expiry_info if it doesn't exist, mirroring deployment arm
                // Only initialize if there are no visits selected (empty terms)
                const hasVisitsSelected = chain_expiry_info?.expiry_condition?.terms?.length > 0
                const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
                if (!chain_expiry_info?.expiry_condition && !chain_expiry_info?.visit_id && !chain_expiry_info?.visits && !hasVisitsSelected && deployArmIds.length > 0) {
                  updateScheduleKey('chain_expiry_info', buildChainExpiryInfo({
                    existing: chain_expiry_info,
                    armIds: deployArmIds,
                    visits: [],
                  }))
                }
              }
            }}
            disabled={isVisitSummary}
            content={isDiary ? STRINGS.neverExpire : STRINGS.whenAllComplete}
          />
        ) : null}
        {!isRelative && isImmediateStart && (
          <>
            <Radio
              id='on-same-day-instrument-starts'
              selected={selected.same_day_send_ideal}
              onClick={() => {
                updateRecurring(null)
                updateExpire({ relative_to_send_ideal: { interval: 1, interval_type: 'days' } })
              }}
              content='On same day Instrument starts'
            />
            <Radio
              id='expire'
              selected={selected.expire_after_send_ideal}
              onClick={() => {
                updateRecurring(null)
                updateExpire({ relative_to_send_ideal: { interval: 2, interval_type: 'days' } })
              }}
              content={_getExpireOnContent({
                ...props,
                selected,
                isRelativeToSendIdeal: true,
              })}
            />
          </>
        )}
        {hasInstrumentUnification
          ? isRelative && (
            <Radio
              id='on-same-day-instrument-starts'
              selected={selected.same_day}
              onClick={() => {
                updateRecurring(null)
                updateExpire({ first_login: { interval: deploy.first_login.interval, interval_type: 'days' } })
              }}
              content='On same day Instrument starts'
            />
          )
          : isRelative &&
          !isDiary && (
            <Radio
              id='on-same-day-instrument-starts'
              selected={selected.same_day}
              onClick={() => {
                updateRecurring(null)
                updateExpire({ first_login: { interval: deploy.first_login.interval, interval_type: 'days' } })
              }}
              content='On same day Instrument starts'
            />
          )}
        {!isDeployedByVisit && (
          <Radio
            selected={selected.expire_on}
            onClick={() => {
              const defaultExpire = deploy.absolute || toUTCMidnightSeconds(moment())
              updateRecurring(null)
              updateExpire(
                isRelative
                  ? { first_login: { interval: deploy.first_login.interval + 1, interval_type: 'days' } }
                  : { absolute: defaultExpire },
              )
            }}
            content={_getExpireOnContent({ ...props, selected })}
          />
        )}
        {isInstrumentToInstrument && (
          <Radio
            id='inst-to-inst-expire-after'
            selected={selected.expire_after}
            onClick={() => {
              updateRecurring(null)
              updateExpire({ chain_expire_offset: deploy.chain_deploy_offset + 1 })
            }}
            content={_getExpireOnContent({ ...props, selected, isChainExpire: true })}
          />
        )}
        {!isInstrumentToInstrument && (
          <>
            <Radio
              id='expire-after'
              selected={selected.repeat_after}
              onClick={() => {
                _updateRecurring(recurring)
              }}
              content={
                <div>
                  <span>Expire after</span>
                  <input
                    className='occurence-input'
                    aria-label='occurence-input'
                    min={0}
                    type='number'
                    id='expire-occurance-input'
                    value={recurring && selected.repeat_after ? recurring.recurrences : ''}
                    onChange={e => {
                      if (recurring && recurring.recurrences) {
                        updateRecurringField('recurrences', Math.floor(Number(e.target.value)) || 1)
                      } else {
                        const newRecurring = {
                          interval: 1,
                          interval_type: 'days',
                          ...recurring,
                          recurrences: 2,
                          scheme: 'n-days',
                          visible_days: 1,
                        }
                        updateRecurring(newRecurring)
                        updateExpire({ never: null })
                      }
                    }}
                  />
                  {_getOccurenceString(recurring ? recurring.recurrences : '')}
                </div>
              }
            />
            {isDeployedByVisit && (
              <div style={{ marginTop: '16px', marginLeft: '24px' }}>
                <InstrumentExpireVisitSelection {...props} isActive={selected.repeat_after} id="expire-after-occurrences" />
              </div>
            )}
          </>
        )}
        {hasInstrumentUnification ? (
          <>
            <Radio
              id='when-all-complete'
              selected={selected.all_completed}
              onClick={() => {
                if (isDeployedByVisit) {
                  const { recurrences, ...restRecurring } = recurring || {}
                  const defaultRecurring =
                    Object.keys(restRecurring).length > 0
                      ? restRecurring
                      : RECURRING_AFTER_TIME_OPTION_DEFAULT['n-days']
                  updateRecurring(defaultRecurring)
                } else {
                  updateRecurring(null)
                }
                updateExpire({ never: null })

                // Enable visit confirmation by default for visit-triggered instruments
                // when "Never expire" is first selected; user can later uncheck it.
                if (isDeployedByVisit && expire.expire_on_visit_confirmation === undefined) {
                  updateExpireField('expire_on_visit_confirmation', true)
                  // Initialize chain_expiry_info if it doesn't exist, mirroring deployment arm
                  const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
                  if (!chain_expiry_info?.study_schedule_id && !chain_expiry_info?.study_schedule_ids && deployArmIds.length > 0) {
                    updateScheduleKey('chain_expiry_info', buildChainExpiryInfo({
                      existing: chain_expiry_info,
                      armIds: deployArmIds,
                      visits: [],
                    }))
                  }
                }
              }}
              disabled={isVisitSummary}
              content={`${STRINGS.neverExpire}`}
            />
            {isDeployedByVisit && (
              <div style={{ marginTop: '16px', marginLeft: '24px' }}>
                <InstrumentExpireVisitSelection {...props} isActive={selected.all_completed} id="never-expire" />
              </div>
            )}
          </>
        ) : null}
        {isDeployedByVisit && <VisitExpireContent selected={selected} isVisitSummary={isVisitSummary} {...props} />}
      </div>
    </div>
  )
}

const sharedPropTypes = {
  isInstrumentToInstrument: PropTypes.bool,
  isRelative: PropTypes.bool,
  isRelativeToInstrumentSentDate: PropTypes.bool,
  updateExpire: PropTypes.func,
}

_getExpireOnContent.propTypes = {
  ...sharedPropTypes,
  isChainExpire: PropTypes.bool,
  updateExpireField: PropTypes.func,
  updateRecurring: PropTypes.func,
}

ExpireContent.propTypes = {
  ...sharedPropTypes,
  isDiary: PropTypes.bool,
  disabled: PropTypes.bool,
  updateRecurringField: PropTypes.func,
}

export default contentConnect(ExpireContent)
